import { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { useSnackbar } from 'notistack';

import { useTheme } from '@mui/material/styles';
import { Alert, Button, Stack, TextField, InputAdornment, IconButton } from '@mui/material';
import { MIcon } from '../../../@material-extend';

import useAuth from '../../../../hooks/useAuth';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
import { deleteCredentials, setCredentials } from '../../../../utils/biometric';
import CloseButton from '../../clockings/ClockingButtons/ClockingButton/CloseButton';

const ChangePasswordForm = ({ args, isNewOrExpired, onLogOut }) => {
  const isMountedRef = useIsMountedRef();
  const { resetPassword, put, user, logout, socketUrl } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { isLargeMobile } = useBreakpoints();
  const [showPasswordOld, setShowPasswordOld] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { spacing } = useTheme();

  const iconShowPwd = (show, setValue) => (
    <InputAdornment position="end">
      <IconButton edge="end" onClick={() => setValue((show) => !show)}>
        <MIcon icon={show ? 'visibility' : 'visibility_off'} variant="secondary" filled />
      </IconButton>
    </InputAdornment>
  );

  const shape = {
    confirmNewPassword: Yup.string()
      .required('Le nouveau mot de passe est requis')
      .oneOf([Yup.ref('newPassword'), null], 'Les mots de passe doivent correspondre')
  };

  shape.newPassword = Yup.string()
    .required('Le nouveau mot de passe est requis')
    .notOneOf([Yup.ref('oldPassword')], 'Le nouveau mot de passe doit être différent');

  if ((args.token && args.token.startsWith('S')) || (user && user.isPwdStrong)) {
    shape.newPassword = shape.newPassword
      .min(5, 'Le mot de passe doit être au moins de 5 caractères')
      .matches(/[0-9]+/, {
        message: 'Le mot de passe doit contenir au moins un chiffre'
      })
      .matches(/[a-z]+/, {
        message: 'Le mot de passe doit contenir au moins une minuscule'
      })
      .matches(/[A-Z]+/, {
        message: 'Le mot de passe doit contenir au moins une majuscule'
      });
  } else {
    shape.newPassword.min(6, 'Le mot de passe doit être au moins de 6 caractères');
  }

  if (!args.token) {
    shape.oldPassword = Yup.string().when('isNewOrExpired', {
      is: () => isNewOrExpired === false, // alternatively: (val) => val == true
      then: (schema) => schema.required(`L'ancien mot de passe est requis`),
      otherwise: (schema) => schema.min(0)
    });
  }

  const schema = Yup.object().shape(shape);

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    },
    validationSchema: schema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        if (args.token) {
          await resetPassword(args.token.substring(1), values.newPassword);
          if (window.env.isApp) {
            await deleteCredentials(socketUrl);
          }
        } else {
          await put('/user', {
            current_password: !isNewOrExpired ? values.oldPassword : null,
            password: values.newPassword,
            password_confirmation: values.confirmNewPassword
          });
          if (window.env.isApp) {
            await deleteCredentials(socketUrl);
            await setCredentials(socketUrl, user.email, values.newPassword);
          }
        }

        if (isMountedRef.current) {
          setSubmitting(false);
          enqueueSnackbar('Mot de passe modifié', {
            variant: 'success',
            action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
          });
          if (args.token) {
            navigate('/login');
          } else if (user.isFirstLogin || user.isPwdExpired) {
            window.location.pathname = window.env.PUBLIC_URL;
          } else {
            navigate('/');
          }
        }
      } catch (error) {
        if (isMountedRef.current) {
          setErrors({ afterSubmit: error.message });
          setSubmitting(false);
        }
      }
    }
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleChangeConfirmNewPwd = (e) => {
    // Enable the submit button as soon as the passwords matches
    getFieldProps('confirmNewPassword').onChange(e);
    formik.setFieldTouched('confirmNewPassword', true, false);
  };

  const handleClick = () => {
    if (user?.id) {
      onLogOut();
      logout();
    } else {
      navigate('/login');
      onLogOut();
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form style={styleForm(spacing)} autoComplete="off" noValidate onSubmit={handleSubmit}>
        {errors.afterSubmit && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {errors.afterSubmit}
          </Alert>
        )}

        {!args.token && !isNewOrExpired && (
          <TextField
            {...getFieldProps('oldPassword')}
            autoComplete="off"
            slotProps={{ endAdornment: iconShowPwd(showPasswordOld, setShowPasswordOld) }}
            id="oldPassword"
            fullWidth
            type={showPasswordOld ? 'text' : 'password'}
            label="Ancien mot de passe"
            error={Boolean(touched.oldPassword && errors.oldPassword)}
            helperText={touched.oldPassword && errors.oldPassword}
          />
        )}

        <TextField
          id="newPassword"
          {...getFieldProps('newPassword')}
          slotProps={{ endAdornment: iconShowPwd(showPasswordNew, setShowPasswordNew) }}
          fullWidth
          type={showPasswordNew ? 'text' : 'password'}
          label="Nouveau mot de passe"
          error={Boolean(touched.newPassword && errors.newPassword)}
          helperText={touched.newPassword && errors.newPassword}
        />

        <TextField
          id="confirmNewPassword"
          {...getFieldProps('confirmNewPassword')}
          autoComplete="off"
          slotProps={{ endAdornment: iconShowPwd(showConfirm, setShowConfirm) }}
          fullWidth
          type={showConfirm ? 'text' : 'password'}
          label="Confirmer le nouveau mot de passe"
          sx={{ label: { pr: 4 } /* required for small mobiles and change pwd card */ }}
          error={Boolean(touched.confirmNewPassword && errors.confirmNewPassword)}
          helperText={touched.confirmNewPassword && errors.confirmNewPassword}
          onChange={handleChangeConfirmNewPwd}
        />

        <Stack sx={styleActions(isLargeMobile)}>
          {onLogOut && <Button onClick={handleClick}>{user?.id ? 'Se déconnecter' : 'Annuler'}</Button>}
          <Button
            id="btn-change-password"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            fullWidth={!args.token && !isNewOrExpired}
            disabled={!!errors.confirmNewPassword || !touched.confirmNewPassword}
            size="large"
          >
            Mettre à jour le mot de passe
          </Button>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

const styleForm = (spacing) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: spacing(3),
  paddingTop: 8
});

const styleActions = (isLargeMobile) => ({
  flexGrow: 1,
  alignSelf: isLargeMobile ? 'stretch' : 'flex-end',
  flexFlow: `${isLargeMobile ? 'column-reverse' : 'row'} nowrap`,
  gap: 2
});

ChangePasswordForm.propTypes = {
  args: PropTypes.object,
  isNewOrExpired: PropTypes.bool,
  hideCard: PropTypes.bool,
  onLogOut: PropTypes.func
};

export default ChangePasswordForm;
